import { ColumnDef } from '@tanstack/react-table'
import { Badge } from '@/common/ui/badge'
import { Button } from '@/common/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/common/ui/dropdown-menu'
import { MoreHorizontal, Edit, Trash } from 'lucide-react'
import { Link } from 'react-router-dom'
import {
	REWARD_TYPE_LABELS,
	REWARD_TYPE_VARIANTS,
	CURRENCY_TYPE_LABELS,
	STATUS_LABELS,
} from '../ShopItemsListing/constants'
import type { components } from '@/schema'

type ShopItem = components['schemas']['AdminShopItemResponse'] & {
	price?: number
}

interface ShopTableColumnsProps {
	onDelete: (id: string) => void
}

export const createShopTableColumns = ({
	onDelete,
}: ShopTableColumnsProps): ColumnDef<ShopItem>[] => [
	{
		accessorKey: 'imageUrl',
		header: 'รูปภาพ',
		cell: ({ row }) => {
			const item = row.original
			return item.imageUrl ? (
				<img
					src={item.imageUrl}
					alt={item.name}
					className="h-10 w-10 rounded object-cover"
				/>
			) : (
				<div className="flex h-10 w-10 items-center justify-center rounded bg-gray-100">
					<span className="text-xs text-gray-500">ไม่มี</span>
				</div>
			)
		},
	},
	{
		accessorKey: 'name',
		header: 'ชื่อสินค้า',
		cell: ({ row }) => {
			const item = row.original
			return (
				<div>
					<div className="font-medium">{item.name}</div>
					{item.description && (
						<div className="line-clamp-1 text-sm text-muted-foreground">
							{item.description}
						</div>
					)}
				</div>
			)
		},
	},

	{
		accessorKey: 'price',
		header: 'ราคา',
		cell: ({ row }) => {
			const item = row.original
			const price = item.price?.toLocaleString() || 'N/A'
			const currency =
				CURRENCY_TYPE_LABELS[
					item.currencyType as keyof typeof CURRENCY_TYPE_LABELS
				] || item.currencyType
			return `${price} ${currency}`
		},
	},
	{
		accessorKey: 'rewardType',
		header: 'ประเภท',
		cell: ({ row }) => {
			const type = row.getValue('rewardType') as string
			const label =
				REWARD_TYPE_LABELS[type as keyof typeof REWARD_TYPE_LABELS] || type
			const variant =
				REWARD_TYPE_VARIANTS[type as keyof typeof REWARD_TYPE_VARIANTS] ||
				'outline'
			return <Badge variant={variant as any}>{label}</Badge>
		},
	},
	{
		accessorKey: 'amount',
		header: 'จำนวนของรางวัล',
		cell: ({ row }) => {
			const amount = row.getValue('amount') as number
			return amount.toLocaleString()
		},
	},
	{
		accessorKey: 'isActive',
		header: 'สถานะ',
		cell: ({ row }) => {
			const isActive = row.getValue('isActive') as boolean
			return (
				<Badge variant={isActive ? 'default' : 'secondary'}>
					{isActive ? STATUS_LABELS.active : STATUS_LABELS.inactive}
				</Badge>
			)
		},
	},
	{
		id: 'actions',
		header: 'จัดการ',
		cell: ({ row }) => {
			const item = row.original
			return (
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant="ghost" size="sm">
							<MoreHorizontal className="h-4 w-4" />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						<DropdownMenuItem asChild>
							<Link to={`/operator/shop/items/${item.id}/edit`}>
								<Edit className="mr-2 h-4 w-4" />
								แก้ไข
							</Link>
						</DropdownMenuItem>
						<DropdownMenuItem
							className="text-red-600"
							onClick={() => onDelete(item.id)}
						>
							<Trash className="mr-2 h-4 w-4" />
							ลบ
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			)
		},
	},
]

export const STATUS_OPTIONS = [
	{ label: 'เปิดใช้งาน', value: 'true' },
	{ label: 'ปิดใช้งาน', value: 'false' },
]

export const REWARD_TYPE_OPTIONS = [
	{ label: 'เครดิต', value: 'CREDIT' },
	{ label: 'ฟรีสปิน', value: 'FREE_SPIN' },
	{ label: 'สินค้า', value: 'ITEM' },
]

export const CURRENCY_TYPE_OPTIONS_FILTER = [
	{ label: 'เพชร', value: 'DIAMOND' },
	{ label: 'เครดิต', value: 'CREDIT' },
]
